import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import React, { useEffect, useState } from 'react'
import { usePhotovoltaicValidation } from '../../hooks/validation'
import { Tooltip } from '@mui/material'
import { ErrorOutlineOutlined } from '@mui/icons-material'
import { PhotovoltaicTrackerModel } from '../../models/photovoltaic'

interface PhotovoltaicTabIconProps {
  tabData: PhotovoltaicTrackerModel
  index: number
  isInactive: boolean
}

/** This React component serves as an icon representation for a photovoltaic (PV) tracker tab
 * It is responsible for displaying an error or warning icon,
 * based on the validation status of the associated PV tracker data.
 *
 * Props:
 * - tabData: PhotovoltaicTrackerModel - Data for the specific PV tracker associated with the tab.
 * - index: number - Index of the tab within the tab bar.
 * - isInactive: boolean - Indicates whether the tab is currently inactive or not in the UI.
 *
 * The component uses translation hooks, validation schemas, and state to dynamically determine
 * and display error or warning icons based on the validation status of the PV tracker data.
 */
const PhotovoltaicTabIcon: React.FC<PhotovoltaicTabIconProps> = ({ tabData, index, isInactive }) => {
  const { t } = useCustomTranslation()
  const { inProgressValidationSchema, finalValidationSchema } = usePhotovoltaicValidation()
  const [validationErrorType, setValidationErrorType] = useState<'error' | 'warning' | null>(null)

  useEffect(() => {
    const performValidation = async (): Promise<void> => {
      const payload = {
        pvTrackers: [tabData],
      }
      const isFinalValidationFailed = !(await finalValidationSchema.isValid(payload))
      const isInProgressValidationFailed = !(await inProgressValidationSchema.isValid(payload))

      if (isFinalValidationFailed) {
        setValidationErrorType('error')
      } else if (isInProgressValidationFailed) {
        setValidationErrorType('warning')
      } else {
        setValidationErrorType(null)
      }
    }

    isInactive ? performValidation() : setValidationErrorType(null)
  }, [isInactive, tabData, finalValidationSchema, inProgressValidationSchema])

  const toolTipTitle =
    validationErrorType === 'error'
      ? t('installation-statement.photovoltaic.tab.icon-tooltip-error')
      : t('installation-statement.photovoltaic.tab.icon-tooltip-warning')

  return validationErrorType ? (
    <Tooltip title={toolTipTitle}>
      <ErrorOutlineOutlined color={validationErrorType} fontSize={'small'} style={{ alignSelf: 'start' }} />
    </Tooltip>
  ) : null
}

export default PhotovoltaicTabIcon
